import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { FP } from "./tokens";
import { fpInputSx } from "./inputStyles";

const isoHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isoPrimerDiaMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const esFechaIso = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim());

/**
 * Diálogo reutilizable de período (fecha de inicio + fecha final).
 *
 *   <DialogoRangoFechas abierto={open} titulo="Formato 606"
 *     onCerrar={...} onConfirmar={({ fechaInicio, fechaFinal }) => ...} />
 */
export default function DialogoRangoFechas({
  abierto,
  titulo = "Seleccionar período",
  mensaje,
  etiquetaInicio = "Fecha de inicio",
  etiquetaFinal = "Fecha final",
  textoConfirmar = "Continuar",
  textoCerrar = "Cancelar",
  fechaInicioInicial,
  fechaFinalInicial,
  cargando = false,
  confirmarDeshabilitado = false,
  children,
  onCerrar,
  onConfirmar,
}) {
  const [fechaInicio, setFechaInicio] = useState(isoPrimerDiaMes);
  const [fechaFinal, setFechaFinal] = useState(isoHoy);
  const [errorFechas, setErrorFechas] = useState("");

  useEffect(() => {
    if (!abierto) return;
    setFechaInicio(esFechaIso(fechaInicioInicial) ? fechaInicioInicial : isoPrimerDiaMes());
    setFechaFinal(esFechaIso(fechaFinalInicial) ? fechaFinalInicial : isoHoy());
    setErrorFechas("");
  }, [abierto, fechaInicioInicial, fechaFinalInicial]);

  const confirmar = () => {
    const inicio = String(fechaInicio || "").trim();
    const final = String(fechaFinal || "").trim();
    if (!esFechaIso(inicio) || !esFechaIso(final)) {
      setErrorFechas("Indique fecha de inicio y fecha final.");
      return;
    }
    if (inicio > final) {
      setErrorFechas("La fecha de inicio no puede ser posterior a la fecha final.");
      return;
    }
    setErrorFechas("");
    onConfirmar?.({ fechaInicio: inicio, fechaFinal: final });
  };

  return (
    <Dialog
      open={Boolean(abierto)}
      onClose={cargando ? undefined : onCerrar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, boxShadow: FP.shadow.md } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>{titulo}</DialogTitle>
      <DialogContent>
        {mensaje ? (
          <DialogContentText sx={{ color: FP.color.textSecondary, mb: 2 }}>{mensaje}</DialogContentText>
        ) : null}
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={etiquetaInicio}
              value={fechaInicio}
              onChange={(e) => {
                setFechaInicio(e.target.value);
                setErrorFechas("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={etiquetaFinal}
              value={fechaFinal}
              onChange={(e) => {
                setFechaFinal(e.target.value);
                setErrorFechas("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
        </Grid>
        {errorFechas ? (
          <DialogContentText sx={{ color: FP.color.danger, mt: 1.5 }}>{errorFechas}</DialogContentText>
        ) : null}
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCerrar}
          disabled={cargando}
          sx={{ textTransform: "none", color: FP.color.textSecondary, fontWeight: 700 }}
        >
          {textoCerrar}
        </Button>
        <Button
          variant="contained"
          disabled={cargando || confirmarDeshabilitado}
          onClick={confirmar}
          sx={{
            bgcolor: FP.color.navy,
            color: "#FFFFFF",
            borderRadius: 1.4,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
          }}
        >
          {cargando ? "Procesando…" : textoConfirmar}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
